import { EditType } from "@components/EditableCell"
import { Row } from "@components/VerticalTable"
import { GraphStoreType } from "@features/graph/Graph"
import PropertiesTable, {
  NodeProperties,
  PropertiesRow,
} from "@features/graph/details/components/PropertiesTable"
import { GraphDataViewModel, GraphEditMode } from "@features/graph/graphViewModel"
import { graphReplaceNode } from "@features/graph/state/graphReplaceNode"
import { deepCopy } from "@features/graph/utils/graphUtils"
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from "@mui/material"
import { enqueueSnackbar } from "notistack"
import React, { SyntheticEvent, useEffect, useState } from "react"
import { Node, useStoreApi } from "reactflow"
import { findNode } from "utils/graph"
import { ParentChildDetails } from "@features/graph/details/components/ParentChildDetails/ParentChildDetails"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useLocalStorage } from "usehooks-ts"
import { NodeRowsAccordion } from "@features/graph/details/components/NodeRowsAccordion"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import { getNodeTypeMap } from "@features/graph/utils/graphNodeUtils"
import { NodeDetailsLabel } from "@features/graph/details/components/NodeDetailsLabel"
import NodeTagsAccordion from "@features/graph/details/components/NodeTagsAccordion"

// https://tkrisk.pgm-np.spglobal.com/docs/graph_creation/nodes_definition.html#categorical-nodes-definition
// example of "properties" to help understand its structure
// {
//   parents_lst: ["n4", "n3", "n5"], // parent node ids, only categorical, discrete nodes and deterministic (not continous!) are allowed
//   a_sample_value: "param1", // one of params of the node or null
//   df_cpd: {
//     distribution: ["Categorical", "Categorical", "Categorical", "Categorical"],
//
//     param1: [0.5, 0.5, 0.5, 0.5], // values between 0 and 1
//     param2: [0.5, 0.5, 0.5, 0.5],
//     param3: [0.5, 0.5, 0.5, 0.5],
//
//     n3: [0, 1, 0, 1], // discrete node parent combinations
//     n4: ["parent_param1", "parent_param1", "parant_param2", "parant_param2"], // categorical node parent combinations
//     n5: [123, null, 44.44, 0], // deterministic parent values (numbers or nulls)
//   },
// }

type CategoricalNodeDetailsProps = {
  nodeId: string
  editMode: GraphEditMode
  graphKey?: string
}
type CategoricalNodeParamRecord = Record<string, number[]>

type CategoricalNodeProperties = NodeProperties<
  CategoricalNodeParamRecord,
  keyof CategoricalNodeParamRecord
>

const customizeRows = (row: PropertiesRow) => {
  const updatedRow = deepCopy(row)

  for (const key in row) {
    if (key === "distribution") continue

    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    updatedRow[key] = { ...row[key], type: EditType.Number }
  }

  return updatedRow
}

const CategoricalNodeDetails = ({ nodeId, editMode, graphKey }: CategoricalNodeDetailsProps) => {
  const graphStore: GraphStoreType = useStoreApi()
  const [node, setNode] = useState<Node>()
  const [expandedPropertiesTable, setExpandedPropertiesTable] = useLocalStorage(
    `nodePropertiesTableAccordionExpanded`,
    true,
  )

  useEffect(() => {
    setNode(findNode(graphStore, nodeId))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [nodeId])

  if (!node) {
    return
  }

  const properties: CategoricalNodeProperties = node.data?.node?.properties ?? {}
  const sampleValue = properties.a_sample_value
  const nodeTypeMap = getNodeTypeMap(GraphDataViewModel.data?.nodeMap ?? {})

  const { unit, description, label, color } = node.data?.display ?? {}

  const nodeRows: Row[] = [
    { label: "Type", value: "Categorical" },
    { label: "Historic Mnemonic", value: node.data?.display["Historic Mnem"] },
    // { label: "Color", children: <ColorPreview color={color} />, value: color },
    { label: "Description", value: node.data.display["Description"] || "-" },
    { label: "Unit", value: formatValuePythonic("", node.data?.display["Unit Format"]) || "-" },
    { label: "Exclude From Export", value: node.data?.display.exportExcluded ? "yes" : "no" },
    { label: "Link", value: node.data.display.Link ?? "" },
  ]

  if (sampleValue) {
    nodeRows.push({ label: "Sample value", value: sampleValue })
  }

  const handleDfCpdFieldCellBlur = async (field: string, newValue: string, index: number) => {
    if (!graphKey) {
      return
    }

    const parsedValue = Number(newValue)

    if (parsedValue > 1 || parsedValue < 0) {
      enqueueSnackbar("Value of the cell has to be between 0 and 1", { variant: "error" })
      return
    }

    node.data.node.properties.df_cpd[field][index] = parsedValue
    await graphReplaceNode(graphStore, graphKey, [node])

    setNode(deepCopy(node))
  }

  return (
    <Grid container direction="column" sx={{ p: 1 }} spacing={1}>
      <Grid item>
        <NodeDetailsLabel label={label} />
      </Grid>
      <NodeRowsAccordion nodeRows={nodeRows} />
      <NodeTagsAccordion tags={node.data.display.tags ?? []} />
      <Grid item sx={{ width: expandedPropertiesTable ? "auto" : "100%" }}>
        <Accordion
          expanded={expandedPropertiesTable}
          onChange={(event: SyntheticEvent, isExpanded: boolean) => {
            setExpandedPropertiesTable(isExpanded)
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>Properties Table</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <PropertiesTable
              type={node.data.node.type}
              editable={editMode === GraphEditMode.SCENARIO}
              labelMap={node.data.labelMap}
              nodeTypeMap={nodeTypeMap}
              properties={properties}
              onBlur={handleDfCpdFieldCellBlur}
              customizeRow={customizeRows}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <ParentChildDetails />
    </Grid>
  )
}

export default CategoricalNodeDetails
